import { BackendRoundProvider, ClientEngine, type RoundProvider } from "@slot/client-engine";
import type { BootLaunchPayload } from "./BootLaunchPayload";
import type { CoreServices } from "./CoreServices";
import type { GamePresentation } from "../composition/GameDefinition";
import type { BootFallbackGridSpec } from "../composition/ports/BootGridPresenterPort";
import type { SystemModalConfig, SystemModalOutcome } from "../system-modal/SystemModalTypes";
import { GameRuntime } from "../composition/GameRuntime";
import { GambleSession } from "../gamble/GambleSession";
import { GambleOfferFlow } from "../gamble/GambleOfferFlow";
import { SpinFailurePresenter } from "../spin/SpinFailurePresenter";
import { SpinMotionControl } from "../spin/SpinMotionControl";
import { SpinRequestBuilder } from "../spin/SpinRequestBuilder";
import { wallClockSleep } from "./utils/wallClockSleep";
import type { Log } from "@slot/presentation";

/**
 * The engine and the runtime around a composed game presentation: the round provider (the backend
 * when configured, else the given fixture provider), the engine over the frame presenter + plugins,
 * the runtime's collaborators (request builder, motion control, failure presenter, the gamble flow
 * when the settings enable it and the game has a gamble panel), the runtime.
 */
export function createRuntime(input: {
  payload: BootLaunchPayload;
  services: CoreServices;
  presentation: GamePresentation;
  bootFallbackGrid: BootFallbackGridSpec;
  /** The offline provider; used when no backend is configured. */
  fixtureProvider: RoundProvider | null;
  onShowSystemModal: (config: SystemModalConfig) => Promise<SystemModalOutcome>;
  log: Log;
}): GameRuntime {
  const { payload, services: s, presentation, log } = input;
  const backend = payload.backendConfig;
  const roundProvider: RoundProvider =
    backend.baseUrl.length > 0
      ? new BackendRoundProvider({
          baseUrl: backend.baseUrl,
          roundRequestPath: backend.roundRequestPath,
          presentationCompletePath: backend.presentationCompletePath,
          gambleRequestPath: backend.gambleRequestPath,
          gambleCompletePath: backend.gambleCompletePath,
          // Decisions 80, 81: the boot's play session, its bearer on every request and its numbers on the spins and settlements.
          ...(payload.playSession ? { session: { playSession: payload.playSession, wait: wallClockSleep } } : {}),
        })
      : input.fixtureProvider ?? throwNoProvider();
  log(backend.baseUrl.length > 0 ? `[createRuntime] backend baseUrl="${backend.baseUrl}" spin="${backend.roundRequestPath}" settle="${backend.presentationCompletePath}" session=${payload.playSession !== null}` : "[createRuntime] no backend URL — the fixture provider drives the rounds");

  const engine = new ClientEngine({
    roundProvider,
    logDebugMessage: log,
    framePresenter: presentation.framePresenter,
    plugins: presentation.plugins,
  });

  const settings = s.initialSettings;
  const gambleSession =
    (settings.gambleEnabled ?? false) && presentation.gamblePresentation
      ? new GambleSession({
          requestGamble: (req) => engine.requestGamble(req),
          presentationPort: presentation.gamblePresentation,
          canvasOverlayStore: s.canvasOverlayStore,
          log,
          onStakeDeducted: (stakeMinor) => s.balanceStore.deductMinorBetFromBalance(stakeMinor),
          onBalanceSnapshot: (snapshot) => s.balanceStore.setBalanceMinor(snapshot.balanceAmount),
          acknowledgeGambleComplete: (roundIdentifier) => engine.acknowledgeGambleComplete({ roundIdentifier }).then((response) => response.accountBalanceSnapshot),
        })
      : null;
  if (gambleSession) log("[createRuntime] gamble session created");

  const runtime = new GameRuntime({
    engine,
    bootGridPresenter: presentation.bootGridPresenter,
    bootFallbackGrid: input.bootFallbackGrid,
    requestBuilder: new SpinRequestBuilder({
      betSystem: s.betSystem,
      balanceStore: s.balanceStore,
      featureModeStore: s.featureModeStore,
      currencyCode: s.moneyContext.currencyCode,
      featurePriceMultipliersById: settings.featurePriceMultipliersById ?? {},
      featureModeMultipliersById: settings.featureModeMultipliersById ?? {},
      log,
    }),
    motion: new SpinMotionControl({
      runner: s.presentationTaskRunner,
      clock: s.presentationClock,
      audioService: s.audioService,
      hapticsService: s.hapticsService,
      mechanicSlamStop: presentation.slam,
      slamScopes: presentation.slamScopes,
      preSpinDropOut: presentation.preSpin,
      log,
    }),
    failure: new SpinFailurePresenter({
      autoSpinStore: s.autoSpinStore,
      balanceStore: s.balanceStore,
      localization: s.localization,
      showSystemModal: input.onShowSystemModal,
      reloadPage: () => window.location.reload(),
      restoreGrid: () => presentation.bootGridPresenter.restoreFallbackGrid(input.bootFallbackGrid),
      log,
    }),
    gamble: gambleSession ? new GambleOfferFlow({ session: gambleSession, currentWinStore: s.currentWinStore, balanceStore: s.balanceStore, log }) : null,
    betSystem: s.betSystem,
    balanceStore: s.balanceStore,
    presentationBarrier: s.presentationBarrier,
    presentationClock: s.presentationClock,
    ...(presentation.beforeRound ? { beforeRound: presentation.beforeRound, timer: (seconds: number) => s.chromeTaskRunner.countdown(seconds) } : {}),
    log,
  });
  // The presentation was built before the coordinator; it reads the phase from the store.
  runtime.getSpinCoordinator().subscribe((snapshot) => s.spinPhaseStore.set(snapshot.phase));
  return runtime;
}

function throwNoProvider(): never {
  throw new Error("createRuntime: no backend URL is configured and no fixture provider was given.");
}
